import { Injectable } from '@angular/core';
import { PermissionNodeInput } from '@control-tower/features/collaborators/models/permission-node.input';
import { PermissionNode } from '../models/permission-node.model';

@Injectable({
  providedIn: 'root',
})
export class PermissionNodeTransformerService {
  /**
   * Build hierarchical structure from flat array
   */
  buildHierarchy(flatNodes: PermissionNode[]): PermissionNode[] {
    const nodeMap = new Map<string, PermissionNode>();
    const rootNodes: PermissionNode[] = [];

    // First pass: create map and add children arrays
    flatNodes.forEach(node => {
      nodeMap.set(node._id, { ...node, children: [] });
    });

    // Second pass: build hierarchy
    flatNodes.forEach(node => {
      const nodeWithChildren = nodeMap.get(node._id)!;

      if (!node.parentId) {
        // Root level node
        rootNodes.push(nodeWithChildren);
      } else {
        // Child node - find parent and add to its children
        const parent = nodeMap.get(node.parentId);
        if (parent && parent.children) {
          parent.children.push(nodeWithChildren);
        }
      }
    });

    // Sort by sortOrder
    const sortNodes = (nodeList: PermissionNode[]): PermissionNode[] => {
      return nodeList
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
          ...node,
          children: node.children ? sortNodes(node.children) : [],
        }));
    };

    return sortNodes(rootNodes);
  }

  /**
   * Flatten hierarchical structure back to flat array
   */
  flattenHierarchy(hierarchicalNodes: PermissionNode[]): PermissionNode[] {
    const flatNodes: PermissionNode[] = [];

    const flatten = (nodes: PermissionNode[]) => {
      nodes.forEach(node => {
        const { children, parent, ...nodeWithoutChildren } = node;
        flatNodes.push(nodeWithoutChildren);
        if (children && children.length > 0) {
          flatten(children);
        }
      });
    };

    flatten(hierarchicalNodes);
    return flatNodes;
  }

  /**
   * Convert PermissionNode tree to PermissionNodeInput tree for backend submission
   */
  convertToPermissionNodeInput(nodes: PermissionNode[]): PermissionNodeInput[] {
    return nodes.map(node => this.convertSingleNodeToInput(node));
  }

  private convertSingleNodeToInput(node: PermissionNode): PermissionNodeInput {
    const inputNode: PermissionNodeInput = {
      nodeId: node.nodeId,
      name: node.name,
      permissionLevels: node.permissionLevels || {
        admin: false,
        editor: false,
        viewer: false,
        adminDisabled: false,
        editorDisabled: false,
        viewerDisabled: false,
      },
      level: node.level,
      parentId: node.parentId,
      expanded: node.expanded,
    };

    // Recursively convert children if they exist
    if (node.children && node.children.length > 0) {
      inputNode.children = node.children.map(child => this.convertSingleNodeToInput(child));
    }

    return inputNode;
  }
}
